/**
 * FillQueueButton - Rellena la cola Spotify con peticiones del evento no reproducidas.
 * Uso: DJ se ausenta (baño, cliente) y quiere asegurarse que la música siga sonando.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

const MINUTES_OPTIONS = [5, 10, 15, 30];

const FillQueueButton = ({ playlistId }) => {
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState(15);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFill = async () => {
    const accessToken = window.spotifyAccessToken;
    if (!accessToken) {
      toast.error('Conecta Spotify primero (Ajustes → Spotify)');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/spotify/fill-queue`,
        { access_token: accessToken, playlist_id: playlistId, minutes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 }
      );
      setResult(res.data);
      toast.success(`✅ ${res.data.added} tracks añadidos (~${res.data.minutes_covered} min)`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(detail);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-red-600 hover:bg-red-700 text-white"
        title="Rellena la cola Spotify con peticiones del evento para cubrir pausas"
        data-testid="fill-queue-btn"
      >
        <AlertTriangle className="w-4 h-4 mr-1" />
        Cubre pausa
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Cubrir pausa
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">
            Añade peticiones del evento (no reproducidas, con Spotify) a la cola de tu dispositivo Spotify Connect activo.
          </p>
          <div className="flex gap-2">
            {MINUTES_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMinutes(m)}
                className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                  minutes === m
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {m} min
              </button>
            ))}
          </div>

          {result && (
            <div className="bg-slate-800/50 rounded p-3 text-sm space-y-1 max-h-56 overflow-y-auto">
              <div className="flex items-center gap-2 text-emerald-400 font-medium">
                <CheckCircle2 className="w-4 h-4" />
                {result.added} añadidos · ~{result.minutes_covered} min cubiertos
              </div>
              {result.tracks_added?.slice(0, 8).map((t, i) => (
                <div key={i} className="text-slate-400 text-xs truncate">
                  ▸ {t.title} — {t.artist}
                </div>
              ))}
              {result.failed > 0 && (
                <div className="text-amber-400 text-xs pt-1">
                  ⚠️ {result.failed} fallaron (sin device activo?)
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading} className="border-slate-600">
              Cerrar
            </Button>
            <Button
              onClick={handleFill}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="fill-queue-confirm"
            >
              {loading ? 'Añadiendo…' : `Cubrir ${minutes} min`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FillQueueButton;
